import { ReactNode, useContext, useEffect, useId, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Trans } from "react-i18next";
import { Link, ListItemText, Tooltip } from "@mui/material";
import { t } from "i18next";
import { ApiClientResponseError } from "@verity/api-client";
import { Lms } from "@verity/api-client/types";
import {
	ExemptedStudent,
	ThemeWrapper,
	StandAloneExemptedStudentsContext,
	useMobileLayout,
	getPlatformTextFromProctoringOptions,
} from "@verity/components";
import {
	CoreButton,
	CoreMenuButton,
	CoreModal,
	CoreSpinner,
	CoreTable,
	CoreTableColumn,
	coreToast,
	CorePaper,
	CoreTitleCard,
	CoreMenuItem,
	CoreSearchField,
	CoreSearchFieldPaper,
} from "@verity/components/core";
import apiClient from "../../../helpers/api-client";
import { getLmsName } from "../../../helpers/lms-name";
import i18n from "../../../i18n";
import { extractAssessmentName, isPortalCreatedThirdPartyAssessment } from "../../../helpers/assessment-name";
import { PortalControllerContext } from "../../context";
import {
	error500,
	genToastInstance,
	toastDismissAndLoad,
	toastUpdateLoad,
	TooltipWrapper,
	replaceWithPortalHostname,
} from "../../shared";
import { isProctorModeDisabled, isStandaloneAssessment, QuizWithProctoringOptions } from "./assessments-page-helper";
import useStartAssessment, { useCheckCompatibility } from "../../start-assessment";
import EditAssessmentOptionsModal from "./editAssessmentOptionPopup";
import { AssessmentFilter, AssessmentFilterButton, assessmentMatches, hasAssessmentFilter } from "./assessment-filter";
import { useLaunchLockdownModal } from "../launch-lockdown-modal";
import InstallExtensionModal from "./install-extension-modal";
import AssessmentInProgressModal from "./assessment-in-progress-modal";
import CreateAssessmentPage from "../create-assessment-page";
import VisualizerBanner from "../visualizer-banner";
import VideoPage from "../video-page";
import VideoPopover from "../video-popover";
import AssessmentDecoyModal from "./assessment-decoy-modal";
import homeStyles from "../../portal.module.css";
import styles from "./instructor-page.module.css";
import WarningIcon from "../../../images/warning.svg?react";
import CreateAssessmentIcon from "../../../images/add.svg?react";
import ContentProtectionPage from "./content-protection";
import { isD2LContentQuizId } from "@verity/intercept-strategy";

const toastInstance = genToastInstance("AssessmentsPage");

const INTRODUCTORY_VIDEO_URLS: Record<Lms, string> = {
	BlackboardLearn: "https://demo.video.yuja.com/V/Video?v=14292126&node=62112794&a=58897967",
	canvas: "https://demo.video.yuja.com/V/Video?v=14292119&node=62112763&a=176984095",
	desire2learn: "https://demo.video.yuja.com/V/Video?v=14292120&node=62112777&a=47274244",
	moodle: "https://demo.video.yuja.com/V/Video?v=14292131&node=62112800&a=77549418",
};

function formatDate(date: Date) {
	return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
		.getDate()
		.toString()
		.padStart(2, "0")}/${date.getFullYear()}`;
}

const Header = () => {
	return (
		<CoreTitleCard
			className={styles.header}
			title={i18n.t(`portal.components.assessmentPageTSX.header.title`)}
			description={i18n.t(`portal.components.assessmentPageTSX.header.description`)}
			slotProps={{ text: { className: styles.headerText, style: { gap: "5px" } } }}
			style={useMobileLayout() ? { padding: "15px 10px 10px 10px " } : undefined}
		/>
	);
};

const EditStandaloneQuizModal = ({
	onClose,
	assessment,
}: {
	onClose: () => void;
	assessment: QuizWithProctoringOptions;
}) => {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteId, lmsProvidedCourseId } = portalContext;

	const [exemptedStudents, setExemptedStudents] = useState<ExemptedStudent[]>([]);
	const [students, setStudents] = useState<{ userId: number; name: string }[]>([]);

	async function loadData() {
		try {
			if (lms && instituteId != null && assessment && "quizId" in assessment && "id" in assessment) {
				const { quizId } = assessment;
				const standaloneStudents = await apiClient.assessment.getStandaloneStudents(assessment.id);
				const exemptedStudents = await apiClient.user.getProctoringExemptionStudents({
					quizId: quizId,
					courseId: lmsProvidedCourseId,
				});

				const formattedStudents = standaloneStudents.map((student) => ({
					userId: student.userId,
					name: student.email,
				}));
				setStudents(formattedStudents);

				const exemptedFormattedStudents = exemptedStudents
					.map(({ userId, proctoringOptionProfileId }) => {
						const student = formattedStudents.find((student) => student.userId === userId);
						if (student) {
							return {
								userId: student.userId,
								name: student.name,
								...(proctoringOptionProfileId ? { proctoringOptionProfileId } : {}),
							};
						}
						return undefined;
					})
					.filter((student): student is ExemptedStudent => student !== undefined);
				setExemptedStudents(exemptedFormattedStudents);
			}
		} catch (error) {
			console.error("Error fetching Verity options:", error);
		}
	}

	useEffect(() => {
		void (async () => {
			await loadData();
		})();
	}, []);

	return (
		<StandAloneExemptedStudentsContext.Provider value={{ studentList: students, exemptedStudents: exemptedStudents }}>
			<EditAssessmentOptionsModal assessment={assessment} onClose={onClose} />
		</StandAloneExemptedStudentsContext.Provider>
	);
};

function DeleteAssessmentConfirmationModal({
	onClose,
	onDelete,
	displayedAssessmentName,
	authorizing,
}: {
	onClose: () => void;
	onDelete: () => Promise<void>;
	displayedAssessmentName: string;
	authorizing?: boolean;
}) {
	const [loading, setLoading] = useState(false);

	return (
		<CoreModal
			open
			onClose={loading ? undefined : () => onClose()}
			title={t("portal.components.assessmentPageTSX.columnList.action.deleteAssessment")}
			buttons={[
				{
					text: t("portal.components.assessmentPageTSX.columnList.deleteModal.cancel"),
					color: "secondary",
					disabled: loading,
					onClick: onClose,
				},
				{
					text: loading
						? authorizing
							? t("portal.components.assessmentPageTSX.columnList.deleteModal.authorizing")
							: t("portal.components.assessmentPageTSX.columnList.deleteModal.deleting")
						: t("portal.components.assessmentPageTSX.columnList.deleteModal.delete"),
					color: "cancel",
					disabled: loading,
					onClick: () => {
						setLoading(true);
						onDelete()
							.then(onClose)
							.catch(console.error)
							.finally(() => setLoading(false));
					},
					startIcon: loading && <CoreSpinner size={12} color="red" />,
				},
			]}
			disableTopCloseButton={loading}
		>
			<span>
				{i18n.t("portal.components.assessmentPageTSX.columnList.deleteModal.description", {
					name: displayedAssessmentName,
				})}
			</span>
		</CoreModal>
	);
}

const AssessmentActionButton = ({
	assessment,
	displayedAssessmentName,
	refreshData,
}: {
	assessment: QuizWithProctoringOptions;
	displayedAssessmentName: string;
	refreshData: () => void;
}) => {
	const { quizId, proctoringOptions } = assessment;

	const {
		portalContext,
		verityInstituteSettings,
		apiIntegrations,
		userPlatformInfo,
		pages,
		navigateToPage,
		changePortalContext,
	} = useContext(PortalControllerContext);
	const { lms, lmsDetails, instituteIntegrationHostname, portalHostname, lmsProvidedCourseId } = portalContext;

	const [menuShowing, setMenuShowing] = useState(false);
	const [configurationModalShowing, setConfigurationModalShowing] = useState(false);
	const [editStandaloneModalShowing, setEditStandaloneModalShowing] = useState(false);
	const [deleteDialogShowing, setDeleteDialogShowing] = useState(false);
	const [contentProtectionShowing, setContentProtectionShowing] = useState(false);
	const [assessmentError, setAssessmentError] = useState<
		| {
				action: "start" | "check-compatibility";
				reason: "in-progress";
		  }
		| undefined
	>(undefined);
	const [decoyModalShowing, setDecoyModalShowing] = useState(false);

	const [extensionInstallInfo, setExtensionInstallInfo] = useState<
		| {
				action: "start" | "check-compatibility";
				reason: "extension" | "incognito" | "managed-guest-session";
		  }
		| undefined
	>(undefined);
	const { modal: launchLockdownModal, setLaunchInfo: setLockdownLaunchInfo } = useLaunchLockdownModal();

	const [authorizingDelete, setAuthorizingDelete] = useState(false);

	const portalCreatedThirdParty = isPortalCreatedThirdPartyAssessment(assessment);
	const standalone = isStandaloneAssessment(assessment);
	const isCanvasAssignment =
		lms === "canvas" &&
		String(
			(
				assessment.quizInfo as
					| {
							type?: string;
					  }
					| undefined
			)?.["type"],
		) === "assignment";
	const isD2lContentQuiz = lms === "desire2learn" && isD2LContentQuizId(assessment.quizId);
	const proctorModeDisabled = isProctorModeDisabled(verityInstituteSettings, proctoringOptions);
	const verityEnabled = proctoringOptions?.enabled;

	const startAssessment = useStartAssessment(
		assessment,
		(reason) => setExtensionInstallInfo({ action: "start", reason }),
		setLockdownLaunchInfo,
	);
	const checkCompatibility = useCheckCompatibility(
		assessment,
		(reason) => setExtensionInstallInfo({ action: "check-compatibility", reason }),
		setLockdownLaunchInfo,
		(reason) => setAssessmentError({ action: "check-compatibility", reason }),
	);

	const redirectToLmsEditPage = async () => {
		if (!lmsProvidedCourseId) {
			return;
		}
		const loadingToastId = toastDismissAndLoad(
			toastInstance,
			t("portal.components.assessmentPageTSX.columnList.redirecting"),
		);
		try {
			const link = await apiClient.lmsInfo.getAssessmentUrl(lmsProvidedCourseId, quizId, "edit");
			window.open(replaceWithPortalHostname(link, instituteIntegrationHostname, portalHostname), "_blank")?.focus();
			toast.dismiss(loadingToastId);
		} catch (err) {
			if (err instanceof ApiClientResponseError && err.status === 501) {
				void toastUpdateLoad(
					toastInstance,
					loadingToastId,
					"error",
					t("portal.components.assessmentPageTSX.editAssessment.featureError"),
				);
				return;
			} else {
				void toastUpdateLoad(
					toastInstance,
					loadingToastId,
					"error",
					t("portal.components.assessmentPageTSX.editAssessment.editError"),
				);
			}
		}
	};

	const editAssessment = async () => {
		if (portalCreatedThirdParty || isD2lContentQuiz) {
			setConfigurationModalShowing(true);
		} else if (standalone) {
			setEditStandaloneModalShowing(true);
		} else {
			await redirectToLmsEditPage();
		}
	};

	const deleteAssessment = async () => {
		try {
			/* Delete assessment on  Verity */
			if (!(await apiClient.lmsInfo.deleteAssessment(assessment.id, false)).success) {
				throw Error(`Failed to delete the assessment.`);
			}

			return coreToast.success(t("portal.components.assessmentPageTSX.deleteAssessment.successToast"), {
				containerId: toastInstance.containerId,
			});
		} catch (err) {
			coreToast.error(t("portal.components.assessmentPageTSX.deleteAssessment.deleteError"), {
				containerId: toastInstance.containerId,
				autoClose: false,
			});
			let message = "Failed to delete the assessment.";
			if (err instanceof Error) {
				message += " " + err.message;
			}
			throw Error(message);
		}
	};

	const dropdownOptions: ReactNode[] = [];
	if (
		!standalone &&
		!proctorModeDisabled &&
		!proctoringOptions?.isLiveProctored &&
		(lms !== "BlackboardLearn" || lmsDetails?.type !== "ultra") &&
		startAssessment
	) {
		dropdownOptions.push(
			<CoreMenuItem
				key="start"
				onClick={() => {
					void startAssessment();
					setMenuShowing(false);
				}}
			>
				<ListItemText primary={t("portal.components.assessmentPageTSX.columnList.action.startAssessment")} />
			</CoreMenuItem>,
		);
	}
	if (
		(userPlatformInfo.platform === "extension" && (lms !== "BlackboardLearn" || lmsDetails?.type !== "ultra")) ||
		portalCreatedThirdParty ||
		standalone
	) {
		dropdownOptions.push(
			<CoreMenuItem
				key="edit"
				onClick={() => {
					void editAssessment();
					setMenuShowing(false);
				}}
			>
				<ListItemText primary={t("portal.components.assessmentPageTSX.columnList.action.editSettings")} />
			</CoreMenuItem>,
		);
	}
	if ("assessmentReport" in pages) {
		dropdownOptions.push(
			<CoreMenuItem
				key="viewResults"
				onClick={async () => {
					await changePortalContext({ assessmentId: assessment.id });
					navigateToPage("assessmentReport");
					setMenuShowing(false);
				}}
			>
				<ListItemText primary={t("portal.components.assessmentPageTSX.columnList.action.viewResult")} />
			</CoreMenuItem>,
		);
	}
	if (verityEnabled) {
		if (checkCompatibility) {
			dropdownOptions.push(
				<CoreMenuItem
					key="checkCompatibility"
					onClick={() => {
						void checkCompatibility();
						setMenuShowing(false);
					}}
				>
					<ListItemText primary={t("portal.components.assessmentPageTSX.columnList.action.checkCompatibility")} />
				</CoreMenuItem>,
			);
		}
		if (
			!standalone &&
			!portalCreatedThirdParty &&
			!isCanvasAssignment &&
			lms !== "moodle" &&
			!isD2lContentQuiz &&
			(lms !== "BlackboardLearn" || lmsDetails?.type === "ultra")
		) {
			dropdownOptions.push(
				<CoreMenuItem
					key="contentProtection"
					onClick={() => {
						setContentProtectionShowing(true);
						setMenuShowing(false);
					}}
				>
					<ListItemText primary={t("portal.components.assessmentPageTSX.columnList.action.contentProtection")} />
				</CoreMenuItem>,
			);

			if (!verityInstituteSettings.liteMode) {
				dropdownOptions.push(
					<CoreMenuItem
						key="decoy"
						onClick={() => {
							setDecoyModalShowing(true);
							setMenuShowing(false);
						}}
						disabled={!assessment.hasDecoySite && !apiIntegrations.course?.edit}
						disabledTooltip={t(
							`portal.components.assessmentPageTSX.columnList.action.tokenNotAvailable.${lms === "canvas" ? "canvas" : (`default.${"integrationSettings" in pages ? "admin" : "instructor"}` as const)}` as const,
						)}
						slotProps={{ tooltip: { placement: "left" } }}
					>
						<ListItemText
							primary={
								assessment.hasDecoySite
									? t("portal.components.assessmentPageTSX.columnList.action.viewDecoy")
									: t("portal.components.assessmentPageTSX.columnList.action.activateDecoy")
							}
						/>
					</CoreMenuItem>,
				);
			}
		}
	}

	dropdownOptions.push(
		<CoreMenuItem
			key="delete"
			onClick={() => {
				setDeleteDialogShowing(true);
				setMenuShowing(false);
			}}
		>
			<ListItemText primary={t("portal.components.assessmentPageTSX.columnList.action.deleteAssessment")} />
		</CoreMenuItem>,
	);

	return (
		<>
			{dropdownOptions.length > 0 && (
				<CoreMenuButton
					menuShowing={menuShowing}
					setMenuShowing={setMenuShowing}
					aria-label={t("portal.components.assessmentPageTSX.columnList.moreOptions")}
					slotProps={{
						menu: {
							transformOrigin: {
								vertical: "bottom",
								horizontal: "center",
							},
							anchorOrigin: {
								vertical: "top",
								horizontal: "left",
							},
						},
					}}
				>
					{dropdownOptions}
				</CoreMenuButton>
			)}
			{configurationModalShowing && (
				<EditAssessmentOptionsModal
					assessment={assessment}
					onClose={() => setConfigurationModalShowing(false)}
					afterSave={refreshData}
				/>
			)}
			{editStandaloneModalShowing && (
				<EditStandaloneQuizModal assessment={assessment} onClose={() => setEditStandaloneModalShowing(false)} />
			)}
			{deleteDialogShowing && (
				<DeleteAssessmentConfirmationModal
					onClose={() => setDeleteDialogShowing(false)}
					onDelete={() =>
						deleteAssessment()
							.then(() => setAuthorizingDelete(false))
							.then(refreshData)
							.catch(console.error)
					}
					displayedAssessmentName={displayedAssessmentName}
					authorizing={authorizingDelete}
				/>
			)}
			{/** Ideally, check assessmentError.action in the title & body to determine which text to use */}
			{assessmentError && assessmentError.reason === "in-progress" && (
				<AssessmentInProgressModal
					onClose={() => setAssessmentError(undefined)}
					title={i18n.t("portal.components.assessmentInProgress.compatibilityTitle")}
					body={i18n.t("portal.components.assessmentInProgress.compatibilityBody")}
				/>
			)}
			{extensionInstallInfo && (
				<InstallExtensionModal
					onClose={() => setExtensionInstallInfo(undefined)}
					reason={extensionInstallInfo.reason}
					action={extensionInstallInfo.action}
					assessment={assessment}
				/>
			)}
			{contentProtectionShowing && (
				<ContentProtectionPage
					onClose={() => setContentProtectionShowing(false)}
					onUpdate={refreshData}
					assessment={assessment}
				/>
			)}
			{launchLockdownModal}
			{decoyModalShowing && (
				<AssessmentDecoyModal
					assessment={assessment}
					onUpdate={refreshData}
					onBack={() => setDecoyModalShowing(false)}
				/>
			)}
		</>
	);
};

const AssessmentTable = ({
	assessments,
	assessmentsLoading,
	refreshData,
}: {
	assessments: Record<number, QuizWithProctoringOptions>;
	assessmentsLoading: boolean;
	refreshData: () => void;
}) => {
	const { portalContext, verityInstituteSettings } = useContext(PortalControllerContext);
	const { lms } = portalContext;

	const [suffix, setSuffix] = useState<string | null>("YuJa Verity for Test Proctoring");

	const [searchFilter, setSearchFilter] = useState("");
	const [filter, setFilter] = useState<AssessmentFilter>({
		type: [],
		method: [],
		platform: ["extension", "lockdown", "mobile", "chromeOs"],
	});
	const searchControlsId = useId();
	const isMobileLayout = useMobileLayout();
	const lmsName = getLmsName(lms);

	useEffect(() => {
		void (async () => {
			setSuffix(await apiClient.institute.getSuffix());
		})();
	}, []);

	const getQuizName = (quiz: QuizWithProctoringOptions) => extractAssessmentName(quiz, suffix ?? undefined);

	const filteredData = useMemo(() => {
		const lowerSearchFilter = searchFilter.toLowerCase();
		return Object.values(assessments).filter((assessment) => {
			const quizName = getQuizName(assessment);
			return (
				(!searchFilter || quizName.toLowerCase().includes(lowerSearchFilter)) && assessmentMatches(filter, assessment)
			);
		});
	}, [assessments, searchFilter, filter, suffix]);

	const getDisabledText = (text: string, proctoringOptions?: QuizWithProctoringOptions["proctoringOptions"]) => {
		if (isProctorModeDisabled(verityInstituteSettings, proctoringOptions)) {
			return `${text} ${t("portal.components.assessmentPageTSX.getDisabledModeText.disabled")}`;
		} else {
			return text;
		}
	};

	const getDisabledModeComponent = (
		text: string,
		proctoringOptions?: QuizWithProctoringOptions["proctoringOptions"],
	) => {
		const plainText = getDisabledText(text, proctoringOptions);
		if (isProctorModeDisabled(verityInstituteSettings, proctoringOptions)) {
			let text1 = "";
			if (proctoringOptions?.isMobile) {
				text1 = t("portal.components.assessmentPageTSX.getDisabledModeText.mobileApp");
			} else if (proctoringOptions?.isLockdown) {
				text1 = t("portal.components.assessmentPageTSX.getDisabledModeText.lockdownApp");
			} else if (proctoringOptions?.isExtension) {
				text1 = t("portal.components.assessmentPageTSX.getDisabledModeText.extension");
			}

			return TooltipWrapper(
				`${t("portal.components.assessmentPageTSX.getDisabledModeText.text1")} ${text1}
																${t("portal.components.assessmentPageTSX.getDisabledModeText.text2")}
																${t("portal.components.assessmentPageTSX.getDisabledModeText.text3")}`,
				<div style={{ display: "flex", placeContent: "center" }}>
					<WarningIcon
						style={{
							minWidth: "17px",
							width: "17px",
							marginLeft: "-22px",
							marginRight: "5px",
						}}
					/>
					{plainText}
				</div>,
			);
		}
		return plainText;
	};

	const getModeText = (options: QuizWithProctoringOptions["proctoringOptions"]) => {
		if (options?.enabled) {
			const isExtension = !!options.isExtension && verityInstituteSettings.allowExtensionPlatform;
			const isLockdown = !!options.isLockdown && verityInstituteSettings.allowLockdownPlatform;
			const isChromeOs = isLockdown && !!options.chromeOsAccess;
			const isMobile = !!options.isMobile && verityInstituteSettings.allowMobilePlatform;

			return getPlatformTextFromProctoringOptions({ isExtension, isLockdown, isMobile, isChromeOs });
		}
		return t("portal.components.assessmentPageTSX.columnList.notProctored");
	};

	const getMethodText = (options: QuizWithProctoringOptions["proctoringOptions"]) => {
		if (options?.isLiveProctored) {
			return i18n.t("portal.components.assessmentPageTSX.columnList.method.live");
		}
		if (options?.isLivePopIn) {
			if (options?.enablePostReview) {
				return i18n.t("portal.components.assessmentPageTSX.columnList.method.pop-in-post-review");
			} else {
				return i18n.t("portal.components.assessmentPageTSX.columnList.method.pop-in");
			}
		}
		return i18n.t("portal.components.assessmentPageTSX.columnList.method.automated");
	};

	const getMethodComponent = (options: QuizWithProctoringOptions["proctoringOptions"]) => {
		const text = getMethodText(options);
		return options?.isLivePopIn && options?.enablePostReview ? (
			<Tooltip title={i18n.t("portal.components.assessmentPageTSX.columnList.popInPostReview")}>
				<span>{text}</span>
			</Tooltip>
		) : (
			text
		);
	};

	const compareDates = (a?: string | number | null, b?: string | number | null) => {
		if (!a || !b) {
			return 0;
		}
		return new Date(a.toString()).getTime() < new Date(b.toString()).getTime() ? 1 : -1;
	};

	const columnList: Record<string, CoreTableColumn<QuizWithProctoringOptions>> = {
		id: {
			header: "ID",
			key: "id",
			cell: (row) => row.data.id,
			descendingComparator: (a, b) => b.id - a.id,
			width: "10%",
			align: "left",
			padding: "16px 10px 16px 24px",
			showTooltip: true,
		},
		name: {
			header: t("portal.components.assessmentPageTSX.columnList.assessmentName"),
			key: "name",
			cell: (row) => {
				const quizName = getQuizName(row.data);
				return TooltipWrapper(
					quizName,
					<div
						style={{
							textOverflow: "ellipsis",
							overflow: "hidden",
							maxWidth: "fit-content",
						}}
					>
						{quizName}
					</div>,
				);
			},
			descendingComparator: (a, b) =>
				getQuizName(a) === "?"
					? -1
					: getQuizName(b) === "?"
						? 1
						: getQuizName(a).toLowerCase() < getQuizName(b).toLowerCase()
							? 1
							: -1,
			width: "30%",
			truncateString: true,
			align: "left",
		},
		dateCreated: {
			header: t("portal.components.assessmentPageTSX.columnList.dateCreated"),
			key: "date_created",
			cell: (row) => {
				if (!row.data.createdAt) {
					return "-";
				}
				return formatDate(new Date(row.data.createdAt.toString()));
			},
			descendingComparator: (a, b) => compareDates(a.createdAt, b.createdAt),
			width: "15%",
			align: "left",
		},
		dateUpdated: {
			header: t("portal.components.assessmentPageTSX.columnList.dateUpdated"),
			key: "date_updated",
			cell: (row) => {
				if (!row.data.updatedAt) {
					return "-";
				}
				return formatDate(new Date(row.data.updatedAt.toString()));
			},
			descendingComparator: (a, b) => compareDates(a.updatedAt, b.updatedAt),
			width: "15%",
			align: "left",
		},
		verityStatus: {
			header: t("portal.components.assessmentPageTSX.columnList.verityStatus"),
			key: "verity_status",
			cell: (row) =>
				getDisabledModeComponent(
					`${
						row.data.proctoringOptions?.enabled
							? i18n.t("portal.components.assessmentPageTSX.searchBar.ariaLabel")
							: i18n.t("portal.components.assessmentPageTSX.columnList.notProctored")
					}`,
					row.data.proctoringOptions,
				),
			width: "15%",
			align: "left",
		},
		proctoringType: {
			header: t("portal.components.assessmentPageTSX.columnList.proctoringType"),
			key: "proctoring_type",
			cell: (row) => {
				let proctoringText = i18n.t("portal.components.assessmentPageTSX.columnList.notProctored");
				const options = row.data.proctoringOptions;
				if (options?.enabled) {
					proctoringText = getModeText(options);
				}
				return getDisabledModeComponent(proctoringText, options);
			},
			descendingComparator: (a, b) => getModeText(b.proctoringOptions).localeCompare(getModeText(a.proctoringOptions)),
			width: "20%",
			align: "left",
			truncateString: true,
			showTooltip: true,
		},
		proctoringMethod: {
			header: t("portal.components.assessmentPageTSX.columnList.proctoringMethod"),
			key: "proctoring_method",
			cell: (row) => getMethodComponent(row.data.proctoringOptions),
			descendingComparator: (a, b) =>
				getMethodText(b.proctoringOptions).localeCompare(getMethodText(a.proctoringOptions)),
			width: "10%",
			align: "left",
		},
		action: {
			header: t("portal.components.assessmentPageTSX.columnList.action.header"),
			key: "action",
			cell: (row) => (
				<AssessmentActionButton
					assessment={row.data}
					displayedAssessmentName={getQuizName(row.data)}
					refreshData={refreshData}
				/>
			),
			width: "10%",
		},
	};

	const columns: CoreTableColumn<QuizWithProctoringOptions>[] = [
		columnList.id,
		columnList.name,
		columnList.dateCreated,
		columnList.dateUpdated,
		columnList.proctoringType,
		columnList.proctoringMethod,
		columnList.action,
	];

	return (
		<CorePaper className={`${styles.tableArea} ${isMobileLayout ? styles.tableAreaCompact : ""}`}>
			{lms && (
				<VideoPopover
					src={INTRODUCTORY_VIDEO_URLS[lms]}
					videoTitle={i18n.t("portal.components.assessmentPageTSX.introductoryVideo.videoTitle", { lmsName })}
					linkText={i18n.t("portal.components.assessmentPageTSX.introductoryVideo.linkText", { lmsName })}
					header={i18n.t("portal.components.assessmentPageTSX.introductoryVideo.popover.header", { lmsName })}
					description={
						<Trans
							i18nKey="portal.components.assessmentPageTSX.introductoryVideo.popover.description"
							components={{
								a: (
									<Link
										href="https://support.yuja.com/hc/en-us/sections/31910540894615-Creating-a-Proctored-Assessment"
										target="_blank"
										rel="noopener noreferrer"
										color="#0B6F85"
									/>
								),
							}}
						/>
					}
				/>
			)}
			{Object.keys(assessments).length > 0 && (
				<CoreSearchFieldPaper>
					<AssessmentFilterButton filter={filter} setFilter={setFilter} />
					<CoreSearchField
						label={i18n.t("portal.components.assessmentPageTSX.searchBar.placeHolder")}
						onSearch={(value) => setSearchFilter(value)}
						ariaControls={searchControlsId}
						totalResults={filteredData.length}
						showSearchButton={!isMobileLayout}
						mobile={isMobileLayout}
						value={searchFilter}
					/>
				</CoreSearchFieldPaper>
			)}
			<CoreTable
				id={searchControlsId}
				data={filteredData}
				columns={columns}
				fontSize={13}
				headerFontSize={13}
				showNoResultsOnEmptyData
				hidePaginationOnSinglePage
				noResultsString={
					searchFilter === "" && !hasAssessmentFilter(filter)
						? t("portal.components.assessmentPageTSX.searchBar.noAssessments")
						: t("portal.components.assessmentPageTSX.searchBar.noSearchResult")
				}
				loading={assessmentsLoading}
				initialSortKey="date_updated"
				initialSortOrder="desc"
				sortRequired
			/>
		</CorePaper>
	);
};

export default function InstructorAssessmentsPage() {
	const { action, portalContext, dismissableUiElements, dismissUiElement } = useContext(PortalControllerContext);
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;

	const [assessmentCreationEnabled, setAssessmentCreationEnabled] = useState(true);
	const [assessments, setAssessments] = useState<Record<number, QuizWithProctoringOptions>>({});
	const [assessmentsLoading, setAssessmentsLoading] = useState(true);
	const [showCreateAssessment, setShowCreateAssessment] = useState(action === "createPracticeQuiz");
	const inMobileLayout = useMobileLayout();
	const lmsName = getLmsName(lms);

	async function fetchAndUpdateAssessments() {
		if (lmsProvidedCourseId) {
			return apiClient.assessment
				.getCourseAssessments(lmsProvidedCourseId)
				.then((courseAssessments) =>
					setAssessments(Object.fromEntries(courseAssessments.map((assessment) => [assessment.id, assessment]))),
				);
		}
	}

	async function fetchAndUpdateData() {
		try {
			if (lmsProvidedCourseId) {
				if (lms && instituteIntegrationHostname) {
					try {
						setAssessmentCreationEnabled(
							(
								await apiClient.proctoringConfiguration.getEnabledOptions({
									assessmentInfo: {
										lms,
										hostname: instituteIntegrationHostname,
										courseId: lmsProvidedCourseId,
									},
								})
							).proctoringMethod.length > 0,
						);
					} catch (e) {
						console.error(e);
					}
				}
				await fetchAndUpdateAssessments();
			}
		} finally {
			setAssessmentsLoading(false);
		}
	}

	useEffect(() => {
		setShowCreateAssessment(false);
		setAssessmentsLoading(true);
		void fetchAndUpdateData();
	}, [lmsProvidedCourseId]);

	if (assessments === null) {
		return <iframe className={homeStyles.iframePage} src={error500}></iframe>;
	} else if (showCreateAssessment) {
		return <CreateAssessmentPage setShowCreateAssessment={setShowCreateAssessment} refreshPage={fetchAndUpdateData} />;
	} else if (lms && dismissableUiElements.has("introVideo")) {
		return (
			<VideoPage
				src={INTRODUCTORY_VIDEO_URLS[lms]}
				videoTitle={i18n.t("portal.components.assessmentPageTSX.introductoryVideo.videoTitle", { lmsName })}
				header={i18n.t("portal.components.assessmentPageTSX.introductoryVideo.header")}
				description={
					<Trans
						i18nKey="portal.components.assessmentPageTSX.introductoryVideo.description"
						values={{ lmsName }}
						components={{
							a: (
								<Link
									href="https://support.yuja.com/hc/en-us/sections/31910540894615-Creating-a-Proctored-Assessment"
									target="_blank"
									rel="noopener noreferrer"
									color="#0B6F85"
								/>
							),
						}}
					/>
				}
				bottomContent={
					<CoreButton
						onClick={() => {
							setShowCreateAssessment(true);
							dismissUiElement("introVideo");
						}}
						className={styles.assessmentButton}
						startIcon={<CreateAssessmentIcon width="24px" height="24px" />}
					>
						{t("portal.components.assessmentPageTSX.createAssessment")}
					</CoreButton>
				}
			/>
		);
	}
	return (
		<ThemeWrapper
			scopedCssBaselineProps={{
				className: styles.pageWrapper,
			}}
		>
			<div className={`${styles.pageContainer} ${inMobileLayout ? styles.pageContainerCompact : ""}`}>
				<VisualizerBanner />
				<div className={styles.assessmentButtonContainer}>
					{assessmentCreationEnabled && (
						<CoreButton
							onClick={() => {
								setShowCreateAssessment(true);
							}}
							className={`${styles.assessmentButton} ${inMobileLayout ? styles.assessmentButtonCompact : ""}`}
							startIcon={<CreateAssessmentIcon width="24px" height="24px" />}
						>
							{t("portal.components.assessmentPageTSX.createAssessment")}
						</CoreButton>
					)}
				</div>
				<div className={`${styles.page} ${inMobileLayout ? styles.pageCompact : ""}`}>
					<Header />
					<AssessmentTable
						assessments={assessments}
						assessmentsLoading={assessmentsLoading}
						refreshData={fetchAndUpdateAssessments}
					/>
				</div>
				{toastInstance.jsxElement}
			</div>
		</ThemeWrapper>
	);
}
